const AMAZON_TAG  = import.meta.env.VITE_AMAZON_AFFILIATE_TAG  || ''
const APPLE_TOKEN = import.meta.env.VITE_APPLE_AFFILIATE_TOKEN || ''
const APPLE_CT    = import.meta.env.VITE_APPLE_CAMPAIGN || 'flow'

const withAmazon = (url) => {
  if (!AMAZON_TAG) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}tag=${encodeURIComponent(AMAZON_TAG)}`
}

const withApple = (url) => {
  if (!APPLE_TOKEN) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}at=${encodeURIComponent(APPLE_TOKEN)}&ct=${encodeURIComponent(APPLE_CT)}`
}

// `appScheme` is only set for services with reliable mobile URL schemes for search.
// Others rely on the OS's Universal Link / App Link handling on the https URL.
// `appScheme` is intentionally NOT set on monetized services (prime, apple-when-set)
// to preserve affiliate tracking — the https URL is required for the click to register.
export const SERVICES = [
  { id: 'netflix',   name: 'Netflix',     short: 'Netflix',   color: '#E50914', logo: 'N',  affiliate: false,
    link: (t) => `https://www.netflix.com/search?q=${encodeURIComponent(t)}`,
    appScheme: (t) => `nflx://www.netflix.com/search?q=${encodeURIComponent(t)}` },
  { id: 'hulu',      name: 'Hulu',        short: 'Hulu',      color: '#1CE783', logo: 'H',  affiliate: false,
    link: (t) => `https://www.hulu.com/search?q=${encodeURIComponent(t)}`,
    appScheme: (t) => `hulu://search?q=${encodeURIComponent(t)}` },
  { id: 'hbo',       name: 'Max',         short: 'Max',       color: '#A855F7', logo: 'M',  affiliate: false,
    link: (t) => `https://play.max.com/search?q=${encodeURIComponent(t)}` },
  { id: 'disney',    name: 'Disney+',     short: 'Disney+',   color: '#1A6FE8', logo: 'D+', affiliate: false,
    link: (t) => `https://www.disneyplus.com/browse/search?q=${encodeURIComponent(t)}` },
  { id: 'prime',     name: 'Prime Video', short: 'Prime',     color: '#00A8E1', logo: 'P',  affiliate: !!AMAZON_TAG,
    link: (t) => withAmazon(`https://www.amazon.com/s?k=${encodeURIComponent(t)}&i=instant-video`) },
  { id: 'apple',     name: 'Apple TV+',   short: 'Apple TV+', color: '#EBEBF5', lightColor: '#1c1c3e', logo: 'tv', affiliate: !!APPLE_TOKEN,
    link: (t) => withApple(`https://tv.apple.com/search?term=${encodeURIComponent(t)}`) },
  { id: 'peacock',   name: 'Peacock',     short: 'Peacock',   color: '#F97316', logo: '🦚', affiliate: false,
    link: (t) => `https://www.peacocktv.com/search?q=${encodeURIComponent(t)}` },
  { id: 'paramount', name: 'Paramount+',  short: 'P+',        color: '#3B82F6', logo: 'P+', affiliate: false,
    link: (t) => `https://www.paramountplus.com/search/${encodeURIComponent(t)}/`,
    appScheme: (t) => `paramountplus:///search?query=${encodeURIComponent(t)}` },
]

export const HAS_AFFILIATES = SERVICES.some(s => s.affiliate)

export const MOODS = [
  { id: 'laugh',  label: 'Laugh out loud',    icon: '😂', query: 'hilarious comedy series or movie that will make me laugh out loud' },
  { id: 'sleep',  label: 'Wind down',         icon: '🌙', query: 'calm soothing slow-burn show great for winding down' },
  { id: 'edge',   label: 'Thriller',          icon: '😬', query: 'gripping edge-of-your-seat psychological thriller' },
  { id: 'cry',    label: 'Good cry',          icon: '😢', query: 'emotional heartbreaking dramatic story' },
  { id: 'action', label: 'Action',            icon: '💥', query: 'high octane action packed movie' },
  { id: 'crime',  label: 'True crime',        icon: '🔍', query: 'true crime documentary series' },
  { id: 'scifi',  label: 'Sci-fi / Fantasy',  icon: '🚀', query: 'epic sci-fi or fantasy world building series' },
  { id: 'short',  label: 'Quick watch',       icon: '⏱️', query: 'great show with short episodes under 30 minutes' },
  { id: 'family', label: 'Family night',      icon: '👨‍👩‍👧', query: 'family friendly movie everyone will enjoy' },
  { id: 'date',   label: 'Date night',        icon: '🥂', query: 'romantic movie or show perfect for date night' },
]

export const DEFAULT_SERVICES = ['netflix', 'hbo', 'prime', 'hulu', 'apple']
export const STORAGE_KEY_SERVICES = 'flow_services'
export const STORAGE_KEY_PREFS    = 'flow_prefs'
export const STORAGE_KEY_REGION   = 'flow_region'

export const STORAGE_KEY_SETUP    = 'flow_setup_done'
export const DEFAULT_REGION = 'US'

export const REGIONS = [
  { code: 'US', name: 'United States',  flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'CA', name: 'Canada',         flag: '🇨🇦' },
  { code: 'AU', name: 'Australia',      flag: '🇦🇺' },
  { code: 'DE', name: 'Germany',        flag: '🇩🇪' },
  { code: 'FR', name: 'France',         flag: '🇫🇷' },
  { code: 'ES', name: 'Spain',          flag: '🇪🇸' },
  { code: 'IT', name: 'Italy',          flag: '🇮🇹' },
  { code: 'NL', name: 'Netherlands',    flag: '🇳🇱' },
  { code: 'JP', name: 'Japan',          flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea',    flag: '🇰🇷' },
  { code: 'HK', name: 'Hong Kong',      flag: '🇭🇰' },
  { code: 'TW', name: 'Taiwan',         flag: '🇹🇼' },
  { code: 'IN', name: 'India',          flag: '🇮🇳' },
  { code: 'MX', name: 'Mexico',         flag: '🇲🇽' },
  { code: 'BR', name: 'Brazil',         flag: '🇧🇷' },
]

export const svcDisplayColor = (s, bgMode) =>
  bgMode === 'light' && s.lightColor ? s.lightColor : s.color
